// @flow

import { useContext, useEffect, useRef } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  CartesianGrid,
  Legend,
  Bar,
  YAxis,
  Label,
  Line,
  Tooltip,
} from 'recharts';
import { ThemeContext } from 'styled-components';
import { useSafeState } from '../../widgets/common/hooks';

import CustomizedYAxisTick from '../common/customized-y-axis-tick';
import CustomizedXAxisTick from '../common/customized-x-axis-tick';
import CustomizedTooltip from '../common/customized-tooltip';
import GraphLegend from '../common/graph-legend';
import { WidgetBodyWrapper } from '../common/styles';
import WidgetWrapper from '../common/widget-wrapper';
import WidgetHeader from '../common/widget-header';

import { DISPLAY_CHART_TYPE } from '../../../utils/widgets-ui';

import type { displayChartTypeEnum } from '../../../utils/widgets-ui';
import type { unitTypeT } from '../../../models/query-response';
import type { chartDataT } from '../../graph-tile';
import type { aggregationEnum } from '../../../constants/graph-options';
import { getDeviceType } from '../../../utils/device-type';

/**
 * Gets the appropriate colour for a chart element based on its index
 * @param {Object} theme - The theme context object
 * @param {string} displayType - Either 'line' or 'bar'
 * @param {number} index - The index of the current metric (0-based)
 * @param {number} totalCount - Total number of metrics of this type
 * @returns {string} The colour to use for this metric
 */
const getChartColour = (theme, displayType, index, totalCount) => {
  if (!theme) return null;
  
  // For single series, use original colour for backwards compatibility
  if (totalCount === 1) {
    return displayType === DISPLAY_CHART_TYPE.LINE
      ? theme.properties['--chart-widget-line']
      : theme.properties['--chart-widget-bar'];
  }
  
  // For multiple series, use round-robin from colour palette
  const colourKey = displayType === DISPLAY_CHART_TYPE.LINE
    ? '--chart-widget-line-colors'
    : '--chart-widget-bar-colors';
  
  const colourPalette = theme.properties[colourKey];
  
  if (!colourPalette || !Array.isArray(colourPalette)) {
    // Fallback to single colour if palette not defined
    return displayType === DISPLAY_CHART_TYPE.LINE
      ? theme.properties['--chart-widget-line']
      : theme.properties['--chart-widget-bar'];
  }
  
  // Round-robin: use modulo to cycle through palette
  return colourPalette[index % colourPalette.length];
};

// TODO widgetPropsT is duplicated becase importing shared types doesn't display in prop table: https://github.com/storybookjs/storybook/issues/11289
type widgetPropsT = {
  /** The title of the chart */
  title: string,
  /** The subtitle of the chart */
  subtitle: string,
  /** The Description to display in the Tooltip */
  description?: string,
  /** Displays the loading state */
  loading?: boolean,
  /** Displays the error state */
  errors?: boolean,
};

type Props = widgetPropsT & {
  /** The data to display */
  metrics: {
    dataKey: string,
    label: string,
    unitType: unitTypeT,
    chartDisplayType: displayChartTypeEnum,
  }[],
  chartData: chartDataT[],
  aggregation: aggregationEnum,
  /** Progress value between 0 and 1 */
  progress?: number,
};

const Chart = ({
  title,
  subtitle,
  description,
  metrics,
  chartData,
  aggregation,
  loading,
  errors,
  progress,
}: Props) => {
  const [deviceType, setDeviceType] = useSafeState(getDeviceType());

  useEffect(() => {
    function handleResize() {
      setDeviceType(getDeviceType());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setDeviceType]);

  const theme = useContext(ThemeContext);
  const sortLineGraphToFront = (a, b) =>
    a.chartDisplayType === DISPLAY_CHART_TYPE.LINE ? 1 : -1;

  // Count metrics by display type for colour selection
  const lineMetricsCount = metrics.filter(m => m.chartDisplayType === DISPLAY_CHART_TYPE.LINE).length;
  const barMetricsCount = metrics.filter(m => m.chartDisplayType === DISPLAY_CHART_TYPE.BAR).length;

  // Create index maps for each display type
  let lineIndex = 0;
  let barIndex = 0;

  return (
    <WidgetWrapper
      loading={loading}
      errors={errors}
      progress={progress}
      header={
        <WidgetHeader
          title={title}
          subtitle={subtitle}
          description={description}
        />
      }
    >
      <WidgetBodyWrapper>
        <ResponsiveContainer width="100%" height={deviceType === 'phone' ? 300 : 700}>
          <ComposedChart
            data={chartData}
            margin={() => {
              if (deviceType === 'desktop') return { top: 30, right: 40, left: 40, bottom: 0 };
              if (deviceType === 'tablet') return { top: 10, right: 10, left: 10, bottom: 0 };
              if (deviceType === 'phone') return { top: 0, right: 0, left: 0, bottom: 0 };
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="index"
              height={60}
              tick={<CustomizedXAxisTick aggregation={aggregation} />}
              interval="preserveStartEnd"
            />
            <Tooltip
              content={
                <CustomizedTooltip
                  aggregation={aggregation}
                  metrics={metrics}
                />
              }
            />
            <Legend content={<GraphLegend metrics={metrics} />} />

            {metrics.sort(sortLineGraphToFront).map((metric) => {
              const { dataKey, chartDisplayType, label, unitType } = metric;

              if (chartDisplayType === DISPLAY_CHART_TYPE.BAR) {
                const barColour = getChartColour(theme, DISPLAY_CHART_TYPE.BAR, barIndex, barMetricsCount);
                barIndex++; // Increment for next bar metric
                
                return [
                  <Bar
                    yAxisId={dataKey}
                    dataKey={dataKey}
                    isAnimationActive={false}
                    fill={barColour}
                  />,
                  <YAxis
                    yAxisId={dataKey}
                    tick={
                      <CustomizedYAxisTick
                        unitType={unitType}
                        textAnchor="end"
                      />
                    }
                  >
                    <Label
                      value={label}
                      angle={-90}
                      position="center"
                      fill={barColour}
                      dx={-50}
                      style={{
                        textTransform: 'capitalize',
                      }}
                    />
                  </YAxis>,
                ];
              }

              if (chartDisplayType === DISPLAY_CHART_TYPE.LINE) {
                const lineColour = getChartColour(theme, DISPLAY_CHART_TYPE.LINE, lineIndex, lineMetricsCount);
                lineIndex++; // Increment for next line metric
                
                return [
                  <Line
                    yAxisId={dataKey}
                    type="monotone"
                    isAnimationActive={false}
                    dataKey={dataKey}
                    stroke={lineColour}
                    fill={lineColour}
                    strokeWidth={4}
                  />,
                  <YAxis
                    yAxisId={dataKey}
                    orientation="right"
                    tick={
                      <CustomizedYAxisTick
                        unitType={unitType}
                        textAnchor="start"
                      />
                    }
                  >
                    <Label
                      value={label}
                      angle={-90}
                      position="center"
                      fill={lineColour}
                      dx={50}
                      style={{
                        textTransform: 'capitalize',
                      }}
                    />
                  </YAxis>,
                ];
              }

              return null;
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </WidgetBodyWrapper>
    </WidgetWrapper>
  );
};

export default Chart;
